'use client';

import { AllocationConstraintResponse } from '@/app/models/allocationConstraint';
import { Collaborator } from '@/app/models/collaborator';
import { useListAllGroupedAllocationConstraintQuery } from '@/app/services/allocationConstraintApi';
import { useSearchPersonQuery } from '@/app/services/personApi';
import AllocationConstraintTable from '@/components/allocationConstraint/AllocationConstraintTable';
import NewEditAllocationConstraint from '@/components/allocationConstraint/newEditAllocationConstraint';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import TextButton from '@/components/common/TextButton';
import { Box, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function AllocationConstraint() {
  const theme = useTheme();
  const { t } = useTranslation(['allocationConstraint', 'common']);
  const [drawerStatus, setDrawerStatus] = useState(false);

  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaborators, isFetching: isLoadingCollaborators } = useSearchPersonQuery(
    {
      searchTerm: collaboratorSearch,
      available: true,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const [selectedCollaborator, setSelectedCollaborator] = useState<AllocationConstraintResponse | null>(null);
  const { data } = useListAllGroupedAllocationConstraintQuery(collaborator?.badge as string);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PageTitle
        title={t('allocation.restriction')}
        titleLabel={` (${t('collaborator.project')})`}
        subtitle={`${t('allocation.restriction.subtitle')}`}
      />

      <Typography component="p" variant="fieldHeader">
        {t('common:collaborator')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          mb: 2,
          alignItems: 'center',
        }}
      >
        <GenericAutoComplete
          value={collaborator}
          onChange={(value) => {
            setCollaborator(value);
          }}
          equalityTest={(option, value) => option?.email === value?.email}
          getOptionLabel={(option) => `${option?.badge} - ${option?.name}`}
          onSearch={(value) => {
            setCollaboratorSearch(value);
          }}
          options={collaboratorSearch !== '' && collaborators?.content ? collaborators?.content : []}
          placeholder={t('input.search.collaborator') || ''}
          loading={isLoadingCollaborators}
          sx={{ width: theme.spacing(50) }}
        />

        <TextButton
          sx={{ ml: 'auto' }}
          text={`${t('new.restriction')}`}
          onClick={() => {
            setDrawerStatus(true);
            setSelectedCollaborator(null);
          }}
        />
      </Box>

      <AllocationConstraintTable
        rows={data || []}
        setSelectedItem={setSelectedCollaborator}
        setDrawerOpen={setDrawerStatus}
      />

      <LateralDrawer open={drawerStatus} setOpen={setDrawerStatus} position="right" size="bigger">
        <NewEditAllocationConstraint setDrawerOpen={setDrawerStatus} selectedCollaborator={selectedCollaborator} />
      </LateralDrawer>
    </Box>
  );
}

export default AllocationConstraint;
